import { useState, useCallback, useEffect, useMemo } from 'react';
import path from 'ramda/es/path';

import { api } from 'app/api';
import usePopupErr from 'app/common/hooks/usePopupErr';

import { GET_VENDING_MACHINES } from 'app/common/api/requests/data/vending/machines/get';
import { GET_VENDING_SETTINGS } from 'app/common/api/requests/data/vending/settings.get';

import * as vendingStatuses from './vendingStatuses';
import type { VendingStatus as VendingStatusValue } from './vendingStatuses';
import spotStatus from './spotStatus';


export type VendingSettings = {
  status: VendingStatusValue
  allowVendingExternalService: boolean
  loading: boolean
}

export const defaultVendingSettings: VendingSettings = {
  status: vendingStatuses.VENDING_DISABLED,
  allowVendingExternalService: false,
  loading: false
}

type VendingStatusData = {
  enabled: boolean;
  hasMachines: boolean;
  allowVendingExternalService: boolean;
};

const defaultVendingStatusData: VendingStatusData = {
  enabled: false,
  hasMachines: false,
  allowVendingExternalService: false,
};

export default function useVendingSettings(): VendingSettings {
  const [popupErr] = usePopupErr();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<VendingStatusData>(defaultVendingStatusData);

  const requestVendingAvailability = useCallback(async () => {
    const res = await api.request(GET_VENDING_SETTINGS, {
      error: 'Не удалось получить настройки вендинга',
    });

    if (res) {
      const enabled = path(['enabled'], res);
      const allowVendingExternalService = path(['allow_vending_external_service'], res);

      if (
        typeof enabled === 'boolean'
        && typeof allowVendingExternalService === 'boolean'
      ) {
        return {
          enabled,
          allowVendingExternalService,
        };
      }
    }
    return null;
  }, []);

  const requestAvailableMachines = useCallback(async () => {
    const res = await api.request(GET_VENDING_MACHINES, {
      error: 'Не удалось получить количество вендинговых аппаратов',
      params: { limit: 1 },
    });

    if (res) {
      const available = path(['meta', 'available'], res);
      return (typeof available === 'number' ? available : null);
    }
    return null;
  }, []);


  const handleRequestStatus = useCallback(async () => {
    setLoading(true);
    const [settings, machines] = await Promise.all([
      requestVendingAvailability(),
      requestAvailableMachines(),
    ]);
    setLoading(false);

    if (settings === null || machines === null) {
      popupErr('Не удалось получить статус вендинга');
      return;
    }
    setData({
      ...settings,
      hasMachines: machines > 0,
    });
  }, [popupErr, requestVendingAvailability, requestAvailableMachines]);


  useEffect(() => {
    handleRequestStatus();
  }, [handleRequestStatus]);

  const status = useMemo(() => {
    return spotStatus(
      data.enabled,
      data.hasMachines,
    );
  }, [data.enabled, data.hasMachines]);

  const memoized = useMemo<VendingSettings>(() => ({
    status,
    allowVendingExternalService: data.allowVendingExternalService,
    loading,
  }), [data.allowVendingExternalService, loading, status]);

  return memoized;
}
